import * as React from 'react';

import {
  useSupplierHistory,
  useSupplierHistoryDetail,
} from '@/api/use-supplier-history';
import type {
  HistoryParticipationDto,
  ParticipationStatutFinalDto,
} from '@/api/supplier-history-client';
import {
  useMarkAnnouncementRead,
  useSupplierAnnouncementDetail,
  useSupplierAnnouncements,
} from '@/api/use-supplier-announcements';
import type { SupplierAnnouncementListItemDto } from '@/api/supplier-announcements-client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RichHtmlView } from '@/components/rich-html-view';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';

// =============================================================================
// history-screen.tsx — HISTORIQUE FOURNISSEUR, onglet « Mes participations » (DBU §3.9, Lot A).
// =============================================================================
// GET /supplier/history/participations — tableau récapitulatif des campagnes passées du fournisseur, TRI
// sur toutes les colonnes (défaut = chronologique inverse, autorité API). Clic « Voir le détail » ouvre la
// vue détaillée (LECTURE SEULE) : TJMs soumis par profil × niveau, compétences déclarées, dates clés.
//
// PÉRIMÈTRE : onglet 1 « Mes participations » (Lot A) + onglet 3 « Annonces JeKo reçues » (R15-COM-07,
// canal descendant LECTURE SEULE, diffusion §2.7.3/§3.8.3). Onglet 2 (« Mes conversations archivées »,
// chat §2.7.4) reste différé. AUCUN export (V1).
//
// Le « journal récapitulatif des actions sensibles » (source audit_entry) est un ÉCART V1 : la lecture
// audit côté API n'existe pas (seam à concevoir, décision ARCH/SECOPS). La vue détail affiche un encart
// HONNÊTE (« indisponible en V1 »), jamais un journal fabriqué.
//
// WCAG 2.1 AA : <table> avec <caption>, <th scope>, tri par bouton dans l'en-tête + aria-sort (annonce du
// sens de tri), statut final en LIBELLÉ TEXTE (badge = renfort, jamais seul porteur d'info), vue détail en
// région titrée navigable au clavier (boutons natifs). Sélection portée en état d'URL (RULE_REF_006 : aucun
// état métier en localStorage). Tokens sémantiques shadcn/Tailwind uniquement.

/** Onglets de l'Historique (DBU §3.9). L'onglet « Annonces JeKo reçues » est livré par R15-COM-07. */
type HistoryTabId = 'participations' | 'announcements';

interface HistoryTabDef {
  readonly id: HistoryTabId;
  readonly label: string;
  readonly tabId: string;
  readonly panelId: string;
}

/** Définition ORDONNÉE des onglets (l'ordre pilote la navigation clavier flèches du tablist). */
const HISTORY_TABS: readonly HistoryTabDef[] = [
  {
    id: 'participations',
    label: 'Mes participations',
    tabId: 'history-tab-participations',
    panelId: 'history-panel-participations',
  },
  {
    id: 'announcements',
    label: 'Annonces JeKo reçues',
    tabId: 'history-tab-announcements',
    panelId: 'history-panel-announcements',
  },
];

export interface SupplierHistoryScreenProps {
  /** Campagne sélectionnée pour la vue détail (état d'URL), ou `null` si le tableau seul est affiché. */
  readonly selectedCampaignId: string | null;
  /** Ouvre/ferme la vue détail (navigation portée par l'adaptateur routeur — état d'URL). */
  readonly onSelectCampaign: (campaignId: string | null) => void;
}

/** Libellé FR du statut final (DBU §3.9) — jamais une couleur seule (WCAG, sens doublé). */
function statutFinalLabel(statut: ParticipationStatutFinalDto): string {
  switch (statut) {
    case 'Soumis':
      return 'Soumis';
    case 'NonSoumis':
      return 'Non soumis';
    case 'Abandonnee':
      return 'Abandonnée';
  }
}

/**
 * Badge de statut final — trois styles FIXES (renfort visuel doublé du libellé texte, WCAG) :
 * Soumis = noir plein · Non soumis = bordure fine · Abandonnée = fond ivoire, texte gris.
 */
function StatutBadge({
  statut,
}: {
  readonly statut: ParticipationStatutFinalDto;
}): React.JSX.Element {
  const style =
    statut === 'Soumis'
      ? 'border-transparent bg-foreground text-background'
      : statut === 'NonSoumis'
        ? 'border-border bg-transparent text-foreground'
        : 'border-border bg-muted/50 text-muted-foreground';
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-md border px-2 py-0.5 text-xs font-medium ${style}`}
    >
      {statutFinalLabel(statut)}
    </span>
  );
}

/** Libellé FR d'un niveau de séniorité (RateSeniorityLevel), avec repli sur la valeur brute. */
function seniorityLabel(level: string): string {
  const labels: Readonly<Record<string, string>> = {
    Debutant: 'Débutant',
    Maitrisant: 'Maîtrisant',
    Expert: 'Expert',
    Referent: 'Référent',
  };
  return labels[level] ?? level;
}

/** Formate une date ISO en date FR courte, ou « — » si absente. */
function formatDate(iso: string | null): string {
  if (iso === null) {
    return '—';
  }
  return new Date(iso).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Date FR « 11 sept. » (jour + mois abrégé, sans année) — colonne date de la chronologie. */
function formatDayMonth(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
  });
}

/**
 * Libellé compact de la période : « 10 → 11 sept. » (même mois), « 30 sept. → 2 oct. » (mois différents),
 * ou une seule date « 7 sept. » si début = fin (ou une seule borne connue). « — » si aucune date.
 */
function formatPeriode(start: string | null, end: string | null): string {
  if (start === null && end === null) {
    return '—';
  }
  if (start !== null && end !== null) {
    const s = new Date(start);
    const e = new Date(end);
    if (s.toDateString() === e.toDateString()) {
      return formatDayMonth(end);
    }
    const sameMonth =
      s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
    return sameMonth
      ? `${String(s.getDate())} → ${formatDayMonth(end)}`
      : `${formatDayMonth(start)} → ${formatDayMonth(end)}`;
  }
  return formatDayMonth((start ?? end) as string);
}

/** Clé + libellé de MOIS (mono majuscules « SEPTEMBRE 2026 ») pour le regroupement de la chronologie. */
function monthKeyOf(iso: string | null): string {
  if (iso === null) {
    return 'sans-date';
  }
  const d = new Date(iso);
  return `${String(d.getFullYear())}-${String(d.getMonth())}`;
}
function monthLabelOf(iso: string | null): string {
  if (iso === null) {
    return 'Sans date';
  }
  return new Date(iso)
    .toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
    .toUpperCase();
}

/**
 * Ligne grise fusionnant cycles saisis et profils : « Cycle 2 · 1 profil saisi »,
 * « Cycles 1 et 2 · 2 profils saisis », ou « Aucun cycle saisi » si aucune saisie.
 */
function cyclesProfilsLabel(
  cyclesSaisis: readonly number[],
  profilsSaisis: number,
): string {
  if (cyclesSaisis.length === 0) {
    return 'Aucun cycle saisi';
  }
  const cyclesPart =
    cyclesSaisis.length === 1
      ? `Cycle ${String(cyclesSaisis[0])}`
      : `Cycles ${cyclesSaisis
          .slice(0, -1)
          .map(String)
          .join(', ')} et ${String(cyclesSaisis[cyclesSaisis.length - 1])}`;
  const profilsPart = `${String(profilsSaisis)} profil${
    profilsSaisis > 1 ? 's' : ''
  } saisi${profilsSaisis > 1 ? 's' : ''}`;
  return `${cyclesPart} · ${profilsPart}`;
}

/** Formate un instant ISO en date + heure FR (journal des actions sensibles), ou « — » si absent. */
function formatDateTime(iso: string | null): string {
  if (iso === null) {
    return '—';
  }
  return new Date(iso).toLocaleString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Libellé FR d'un type d'action sensible (R15-GOV-07) — jamais le code technique brut au premier plan.
 * Repli sur le type brut pour tout code inconnu (jamais un libellé inventé).
 */
function sensitiveActionLabel(eventType: string): string {
  const labels: Readonly<Record<string, string>> = {
    'DE.SubmissionCloned': 'Saisie créée par duplication',
    'DE.SubmissionSubmitted': 'Réponse soumise',
    'DE.SubmissionLocked': 'Réponse verrouillée',
  };
  return labels[eventType] ?? eventType;
}

export function SupplierHistoryScreen({
  selectedCampaignId,
  onSelectCampaign,
}: SupplierHistoryScreenProps): React.JSX.Element {
  const query = useSupplierHistory();
  const [activeTab, setActiveTab] = React.useState<HistoryTabId>('participations');
  const tabRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  /**
   * Navigation clavier du tablist (motif WAI-ARIA « tabs » à activation manuelle) : flèches gauche/droite
   * (+ Home/Fin) déplacent le focus ET activent l'onglet (roving tabindex). Enrichit l'existant sans le
   * régresser (WCAG 2.1 AA — l'onglet gère aussi Entrée/Espace nativement, boutons).
   */
  const onTabKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, index: number): void => {
      const last = HISTORY_TABS.length - 1;
      let next = index;
      if (event.key === 'ArrowRight') {
        next = index === last ? 0 : index + 1;
      } else if (event.key === 'ArrowLeft') {
        next = index === 0 ? last : index - 1;
      } else if (event.key === 'Home') {
        next = 0;
      } else if (event.key === 'End') {
        next = last;
      } else {
        return;
      }
      event.preventDefault();
      const target = HISTORY_TABS[next];
      if (target !== undefined) {
        setActiveTab(target.id);
        tabRefs.current[next]?.focus();
      }
    },
    [],
  );

  // Données déjà triées chronologiquement inverse par l'API (la plus récente en tête) — plus de tri client.
  const rows = query.data?.participations ?? [];

  return (
    <section className="flex flex-col gap-6" aria-labelledby="history-title">
      <header className="flex flex-col gap-1">
        <h1
          id="history-title"
          className="text-2xl font-semibold tracking-tight text-foreground"
        >
          Historique
        </h1>
        <p className="text-sm text-muted-foreground">
          Vos participations passées, de la plus récente à la plus ancienne.
        </p>
      </header>

      {/* Deux onglets (DBU §3.9) : « Mes participations » (Lot A) + « Annonces JeKo reçues » (R15-COM-07).
          L'onglet « Mes conversations archivées » (chat §2.7.4) reste différé. Activation manuelle,
          roving tabindex, navigation clavier flèches (motif WAI-ARIA « tabs »). */}
      <div
        role="tablist"
        aria-label="Sections de l'historique"
        className="flex gap-1 border-b border-border"
      >
        {HISTORY_TABS.map((tab, index) => {
          const selected = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={tab.tabId}
              aria-selected={selected}
              aria-controls={tab.panelId}
              tabIndex={selected ? 0 : -1}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              onClick={() => setActiveTab(tab.id)}
              onKeyDown={(event) => onTabKeyDown(event, index)}
              className={
                selected
                  ? 'border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                  : 'border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
              }
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'participations' ? (
        <div
          id="history-panel-participations"
          role="tabpanel"
          aria-labelledby="history-tab-participations"
          className="space-y-4"
        >
          {query.isLoading ? (
            <p role="status" className="text-sm text-muted-foreground">
              Chargement de l'historique…
            </p>
          ) : null}

          {query.isError ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-foreground"
            >
              Impossible de charger l'historique : {query.error.message}
            </p>
          ) : null}

          {query.isSuccess ? (
            <ParticipationsTimeline
              rows={rows}
              selectedCampaignId={selectedCampaignId}
              onSelectCampaign={onSelectCampaign}
            />
          ) : null}

          {selectedCampaignId !== null ? (
            <ParticipationDetail
              campaignId={selectedCampaignId}
              onClose={() => onSelectCampaign(null)}
            />
          ) : null}
        </div>
      ) : (
        <AnnouncementsPanel />
      )}
    </section>
  );
}

/** Un groupe MENSUEL de participations (chronologie). */
interface MonthGroup {
  readonly key: string;
  readonly label: string;
  readonly rows: HistoryParticipationDto[];
}

/** Regroupe les participations (déjà triées inverse-chrono par l'API) par mois, ordre préservé. */
function groupByMonth(
  rows: readonly HistoryParticipationDto[],
): readonly MonthGroup[] {
  const groups: MonthGroup[] = [];
  for (const p of rows) {
    const dateIso = p.periodeStart ?? p.periodeEnd;
    const key = monthKeyOf(dateIso);
    const last = groups[groups.length - 1];
    if (last !== undefined && last.key === key) {
      last.rows.push(p);
    } else {
      groups.push({ key, label: monthLabelOf(dateIso), rows: [p] });
    }
  }
  return groups;
}

/**
 * Chronologie « Mes participations » (redesign 2026-09-12) : en-têtes de MOIS (mono maj.), grille
 * 2 colonnes [date 150px, alignée à droite, mono, filet vertical | contenu 1fr]. Ligne = titre serif +
 * statut (badge à droite) + ligne grise cycles·profils + lien « Détail → » ; la LIGNE ENTIÈRE est
 * cliquable (ouvre le détail lecture seule). Filet horizontal fin entre lignes, pas de carte. Tri retiré
 * (données déjà ordonnées par date). Clients bénéficiaires retirés (toujours « sous réserve de diffusion »).
 */
function ParticipationsTimeline({
  rows,
  selectedCampaignId,
  onSelectCampaign,
}: {
  readonly rows: readonly HistoryParticipationDto[];
  readonly selectedCampaignId: string | null;
  readonly onSelectCampaign: (campaignId: string | null) => void;
}): React.JSX.Element {
  if (rows.length === 0) {
    return (
      <p role="status" className="py-8 text-center text-sm text-foreground">
        Vous n'avez encore participé à aucune campagne de référencement.
      </p>
    );
  }

  const groups = groupByMonth(rows);

  return (
    <div className="flex flex-col gap-6">
      {groups.map((group) => (
        <div key={group.key} className="flex flex-col" role="group" aria-label={group.label}>
          {/* Libellé de mois : repère VISUEL (pas un titre → évite une rupture d'ordre de headings). */}
          <p className="mb-1 font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {group.label}
          </p>
          <ul className="flex flex-col">
            {group.rows.map((p) => {
              const selected = p.campaignId === selectedCampaignId;
              const abandoned = p.statutFinal === 'Abandonnee';
              return (
                <li key={p.campaignId}>
                  <button
                    type="button"
                    onClick={() => onSelectCampaign(p.campaignId)}
                    aria-current={selected ? 'true' : undefined}
                    aria-label={`Voir le détail de ${p.intitule}`}
                    className={cn(
                      'group grid w-full grid-cols-[150px_1fr] items-stretch gap-4 border-b border-border/60 py-4 text-left transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                      selected && 'bg-accent/40',
                    )}
                  >
                    {/* Colonne date : mono, alignée à droite, filet vertical continu à droite. */}
                    <span
                      className={cn(
                        'border-r border-border pr-4 text-right font-mono text-sm tabular-nums',
                        abandoned
                          ? 'text-muted-foreground/70'
                          : 'text-muted-foreground',
                      )}
                    >
                      {formatPeriode(p.periodeStart, p.periodeEnd)}
                    </span>
                    {/* Contenu : titre serif + statut à droite ; sous-ligne grise cycles·profils + Détail →. */}
                    <span className="flex items-start justify-between gap-3">
                      <span className="flex flex-col gap-0.5">
                        <span className="font-serif text-base leading-tight text-foreground">
                          {p.intitule}
                        </span>
                        <span
                          className={cn(
                            'text-sm',
                            abandoned
                              ? 'text-muted-foreground/80'
                              : 'text-muted-foreground',
                          )}
                        >
                          {cyclesProfilsLabel(p.cyclesSaisis, p.profilsSaisis)}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-4">
                        <StatutBadge statut={p.statutFinal} />
                        <span
                          aria-hidden="true"
                          className="text-sm text-muted-foreground transition-colors group-hover:text-foreground"
                        >
                          Détail →
                        </span>
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

function ParticipationDetail({
  campaignId,
  onClose,
}: {
  readonly campaignId: string;
  readonly onClose: () => void;
}): React.JSX.Element {
  const query = useSupplierHistoryDetail(campaignId);

  return (
    <Card aria-labelledby="detail-title">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex flex-col gap-1">
            <CardTitle id="detail-title">
              {query.data ? query.data.intitule : 'Détail de la participation'}
            </CardTitle>
            <CardDescription>
              Récapitulatif en lecture seule de votre participation.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>
            Fermer le détail
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {query.isLoading ? (
          <p role="status" className="text-sm text-muted-foreground">
            Chargement du détail…
          </p>
        ) : null}

        {query.isError ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-foreground"
          >
            Impossible de charger le détail : {query.error.message}
          </p>
        ) : null}

        {query.isSuccess ? (
          <>
            {/* Dates clés du cycle (DBU §3.9). */}
            <section aria-labelledby="detail-dates-title" className="flex flex-col gap-2">
              <h3
                id="detail-dates-title"
                className="text-sm font-semibold text-foreground"
              >
                Dates clés du cycle
              </h3>
              <dl className="grid grid-cols-1 gap-1 text-sm sm:grid-cols-2">
                <div className="flex justify-between gap-2 sm:justify-start sm:gap-3">
                  <dt className="text-muted-foreground">Lancement</dt>
                  <dd className="tabular-nums text-foreground">
                    {formatDate(query.data.launchAt)}
                  </dd>
                </div>
                {query.data.cycles.map((c) => (
                  <div
                    key={c.cycleNumber}
                    className="flex justify-between gap-2 sm:justify-start sm:gap-3"
                  >
                    <dt className="text-muted-foreground">
                      Clôture cycle {c.cycleNumber}
                    </dt>
                    <dd className="tabular-nums text-foreground">
                      {formatDate(c.closeAt)}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>

            {/* Récapitulatif des TJMs soumis (LECTURE SEULE, DBU §3.9). */}
            <section aria-labelledby="detail-tjm-title" className="flex flex-col gap-2">
              <h3
                id="detail-tjm-title"
                className="text-sm font-semibold text-foreground"
              >
                TJMs soumis (lecture seule)
              </h3>
              {query.data.tjms.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Aucun TJM n'a été soumis pour cette participation.
                </p>
              ) : (
                <table className="ds-table w-full border-collapse text-sm">
                  <caption className="sr-only">
                    Tarifs journaliers moyens soumis par profil et par niveau de séniorité
                  </caption>
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground">
                      <th scope="col" className="py-2 pr-4 font-medium">
                        Profil
                      </th>
                      <th scope="col" className="py-2 pr-4 font-medium">
                        Niveau
                      </th>
                      <th scope="col" className="py-2 font-medium">
                        TJM soumis
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {query.data.tjms.map((cell) => (
                      <tr
                        key={`${cell.profileCode}-${cell.seniorityLevel}`}
                        className="border-b border-border/60"
                      >
                        <th
                          scope="row"
                          className="py-2 pr-4 text-left font-normal text-foreground"
                        >
                          {cell.profileLabel ?? cell.profileCode}
                        </th>
                        <td className="py-2 pr-4 text-foreground">
                          {seniorityLabel(cell.seniorityLevel)}
                        </td>
                        <td className="py-2 tabular-nums text-foreground">
                          {cell.tjmValueEur} €
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {/* Compétences spécifiques déclarées (DBU §3.9). */}
            <section aria-labelledby="detail-skills-title" className="flex flex-col gap-2">
              <h3
                id="detail-skills-title"
                className="text-sm font-semibold text-foreground"
              >
                Compétences spécifiques déclarées
              </h3>
              {query.data.skills.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Aucune compétence spécifique déclarée.
                </p>
              ) : (
                <ul className="flex flex-col gap-1 text-sm text-foreground">
                  {query.data.skills.map((s) => (
                    <li key={`${s.profileCode}-${s.skillCode}`}>
                      <span className="text-muted-foreground">{s.profileCode} : </span>
                      {s.skillCode}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Journal de mes actions sensibles (R15-GOV-07) — saisies/soumissions/signatures campagne-
                scopées, cloisonnées au tenant de session (le fournisseur ne voit QUE SES actions). Repli
                HONNÊTE (encart neutre) si le seam audit est indisponible — jamais un journal fabriqué. */}
            <section
              aria-labelledby="detail-journal-title"
              className="flex flex-col gap-2"
            >
              <h3
                id="detail-journal-title"
                className="text-sm font-semibold text-foreground"
              >
                Journal de mes actions
              </h3>
              {query.data.journalStatut === 'indisponible' ? (
                <p
                  role="note"
                  className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground"
                >
                  Le journal de vos actions est momentanément indisponible.
                </p>
              ) : query.data.journal.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Aucune action sensible enregistrée pour cette participation.
                </p>
              ) : (
                <table className="ds-table w-full border-collapse text-sm">
                  <caption className="sr-only">
                    Journal chronologique de vos actions sensibles sur cette campagne, plus récentes en tête
                  </caption>
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground">
                      <th scope="col" className="py-2 pr-4 font-medium">
                        Action
                      </th>
                      <th scope="col" className="py-2 font-medium">
                        Date et heure
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {query.data.journal.map((action, index) => (
                      <tr
                        key={`${action.eventType}-${action.occurredAt}-${String(index)}`}
                        className="border-b border-border/60"
                      >
                        <th
                          scope="row"
                          className="py-2 pr-4 text-left font-normal text-foreground"
                        >
                          <span className="flex items-center gap-2">
                            <Badge variant="outline">
                              {sensitiveActionLabel(action.eventType)}
                            </Badge>
                          </span>
                        </th>
                        <td className="py-2 tabular-nums text-foreground">
                          {formatDateTime(action.occurredAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Onglet « Annonces JeKo reçues » (R15-COM-07, DBU §3.8.3 canal descendant / §3.9).
// =============================================================================
// Canal DESCENDANT LECTURE SEULE : le fournisseur consulte les annonces qui LUI ont été diffusées (COM-03)
// et accuse leur lecture (read_at). AUCUN contrôle de réponse/édition/suppression (unidirectionnel, DBU
// l.3318). Liste triée date d'envoi décroissante (autorité API), filtre par campagne (client-side, parité
// du tri client des participations), détail `content_rich` rendu SANITIZÉ (RichHtmlView : re-nettoyage au
// read, aucun `dangerouslySetInnerHTML` non gardé — défense en profondeur du sanitize-on-write serveur).

/** Libellé + variante de badge du statut lu/non-lu (WCAG : le libellé texte porte l'info, badge = renfort). */
function readStatusLabel(readAt: string | null): string {
  return readAt === null ? 'Non lue' : 'Lue';
}

/** Panneau de l'onglet « Annonces JeKo reçues ». Chargé À l'activation de l'onglet (composant monté). */
function AnnouncementsPanel(): React.JSX.Element {
  const [campaignFilter, setCampaignFilter] = React.useState<string>('');
  const [selectedAnnouncementId, setSelectedAnnouncementId] = React.useState<
    string | null
  >(null);
  // Onglet actif au montage du panneau ⇒ requête activée. Liste NON filtrée serveur : le filtre campagne
  // est appliqué client-side (comme le tri des participations), pour garder une liste d'options stable.
  const query = useSupplierAnnouncements(null, true);

  const allItems = React.useMemo(
    () => (query.data ? query.data.items : []),
    [query.data],
  );

  // Options de campagne (distinctes) construites sur l'ensemble reçu (intitulé si résolu, sinon ignorées).
  const campaignOptions = React.useMemo(() => {
    const seen = new Map<string, string>();
    for (const it of allItems) {
      if (it.campaignIntitule !== null && !seen.has(it.campaignId)) {
        seen.set(it.campaignId, it.campaignIntitule);
      }
    }
    return [...seen.entries()].map(([campaignId, intitule]) => ({
      campaignId,
      intitule,
    }));
  }, [allItems]);

  const items = React.useMemo(
    () =>
      campaignFilter === ''
        ? allItems
        : allItems.filter((it) => it.campaignId === campaignFilter),
    [allItems, campaignFilter],
  );

  return (
    <div
      id="history-panel-announcements"
      role="tabpanel"
      aria-labelledby="history-tab-announcements"
      className="space-y-4"
    >
      {query.isLoading ? (
        <p role="status" className="text-sm text-muted-foreground">
          Chargement des annonces…
        </p>
      ) : null}

      {query.isError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-foreground"
        >
          Impossible de charger les annonces : {query.error.message}
        </p>
      ) : null}

      {query.isSuccess ? (
        <AnnouncementsTable
          rows={items}
          campaignOptions={campaignOptions}
          campaignFilter={campaignFilter}
          onCampaignFilter={setCampaignFilter}
          selectedAnnouncementId={selectedAnnouncementId}
          onSelect={setSelectedAnnouncementId}
        />
      ) : null}

      {selectedAnnouncementId !== null ? (
        <AnnouncementDetail
          announcementId={selectedAnnouncementId}
          onClose={() => setSelectedAnnouncementId(null)}
        />
      ) : null}
    </div>
  );
}

/** Tableau des annonces reçues (Titre · Campagne d'origine · Date d'envoi · Statut) + filtre campagne. */
function AnnouncementsTable({
  rows,
  campaignOptions,
  campaignFilter,
  onCampaignFilter,
  selectedAnnouncementId,
  onSelect,
}: {
  readonly rows: readonly SupplierAnnouncementListItemDto[];
  readonly campaignOptions: readonly { campaignId: string; intitule: string }[];
  readonly campaignFilter: string;
  readonly onCampaignFilter: (campaignId: string) => void;
  readonly selectedAnnouncementId: string | null;
  readonly onSelect: (announcementId: string) => void;
}): React.JSX.Element {
  return (
    <Card aria-labelledby="announcements-title">
      <CardHeader>
        <CardTitle id="announcements-title">Annonces JeKo reçues</CardTitle>
        <CardDescription>
          Annonces diffusées par JeKo pour vos campagnes. Consultation en lecture seule.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filtre par campagne (DBU §3.9 l.3414) — libellé explicite, natif clavier. */}
        {campaignOptions.length > 0 ? (
          <div className="flex flex-col gap-1 sm:max-w-xs">
            <label
              htmlFor="announcement-campaign-filter"
              className="text-sm font-medium text-foreground"
            >
              Filtrer par campagne
            </label>
            <select
              id="announcement-campaign-filter"
              value={campaignFilter}
              onChange={(e) => onCampaignFilter(e.target.value)}
              className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <option value="">Toutes les campagnes</option>
              {campaignOptions.map((c) => (
                <option key={c.campaignId} value={c.campaignId}>
                  {c.intitule}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {rows.length === 0 ? (
          <p role="status" className="text-center text-sm text-foreground">
            Aucune annonce reçue.
          </p>
        ) : (
          <table className="ds-table w-full border-collapse text-sm">
            <caption className="sr-only">
              Annonces diffusées par JeKo pour vos campagnes, triées par date d'envoi décroissante
            </caption>
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th scope="col" className="py-2 pr-4 font-medium">
                  Titre
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  Campagne d'origine
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  Date d'envoi
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                  Statut
                </th>
                <th scope="col" className="py-2 font-medium">
                  <span className="sr-only">Action</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr
                  key={a.deliveryId}
                  aria-current={
                    a.announcementId === selectedAnnouncementId ? 'true' : undefined
                  }
                  className="border-b border-border/60"
                >
                  <th
                    scope="row"
                    className="py-3 pr-4 text-left font-medium text-foreground"
                  >
                    {a.titre ?? 'Annonce'}
                  </th>
                  <td className="py-3 pr-4 text-muted-foreground">
                    {a.campaignIntitule ?? '—'}
                  </td>
                  <td className="py-3 pr-4 tabular-nums text-foreground">
                    {formatDate(a.sentAt)}
                  </td>
                  <td className="py-3 pr-4">
                    <Badge variant={a.readAt === null ? 'secondary' : 'outline'}>
                      {readStatusLabel(a.readAt)}
                    </Badge>
                  </td>
                  <td className="py-3 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onSelect(a.announcementId)}
                      aria-label={`Consulter l'annonce ${a.titre ?? 'sans titre'}`}
                    >
                      Consulter
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Détail d'une annonce reçue (LECTURE SEULE). À l'ouverture, marque l'annonce lue (accusé read_at,
 * idempotent). Le contenu enrichi est rendu via RichHtmlView (re-sanitizé au read : `<script>`, `on*`,
 * `javascript:` neutralisés — DOM inerte). AUCUN contrôle de réponse/édition (canal descendant).
 */
function AnnouncementDetail({
  announcementId,
  onClose,
}: {
  readonly announcementId: string;
  readonly onClose: () => void;
}): React.JSX.Element {
  const query = useSupplierAnnouncementDetail(announcementId);
  const markRead = useMarkAnnouncementRead();
  const markMutate = markRead.mutate;

  // Accusé de lecture À l'ouverture (une fois par annonce ouverte). Idempotent côté serveur.
  React.useEffect(() => {
    markMutate(announcementId);
  }, [announcementId, markMutate]);

  return (
    <Card aria-labelledby="announcement-detail-title">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex flex-col gap-1">
            <CardTitle id="announcement-detail-title">
              {query.data ? query.data.titre ?? 'Annonce' : 'Annonce'}
            </CardTitle>
            <CardDescription>
              Annonce JeKo en lecture seule
              {query.data && query.data.sentAt !== null
                ? ` — envoyée le ${formatDate(query.data.sentAt)}`
                : ''}
              .
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>
            Fermer l'annonce
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {query.isLoading ? (
          <p role="status" className="text-sm text-muted-foreground">
            Chargement de l'annonce…
          </p>
        ) : null}

        {query.isError ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-foreground"
          >
            Impossible de charger l'annonce : {query.error.message}
          </p>
        ) : null}

        {query.isSuccess ? (
          <section
            aria-labelledby="announcement-content-title"
            className="flex flex-col gap-2"
          >
            <h3
              id="announcement-content-title"
              className="text-sm font-semibold text-foreground"
            >
              Contenu de l'annonce
            </h3>
            {/* Rendu SANITIZÉ (re-nettoyage au read) — jamais de HTML brut injecté. */}
            <RichHtmlView
              html={query.data.contentRich}
              ariaLabel="Contenu de l'annonce"
            />
          </section>
        ) : null}
      </CardContent>
    </Card>
  );
}

